'use client';

import MetricLineChart from '../MetricLineChart';
import MetricGauge from '../MetricGauge';
import MetricBarChart from '../MetricBarChart';

// Sample data: Debt ratio trend over time
const debtTrendData = [
  { name: 'Q1', ratio: 12.4 },
  { name: 'Q2', ratio: 10.8 },
  { name: 'Q3', ratio: 9.2 },
  { name: 'Q4', ratio: 7.6 },
  { name: 'Q5', ratio: 6.8 },
  { name: 'Q6', ratio: 7.2 },
];

// Sample data: Debt by module
const moduleDebtData = [
  { name: 'Auth', ratio: 45, priority: 'Critical' },
  { name: 'Payments', ratio: 38, priority: 'Critical' },
  { name: 'API Controllers', ratio: 18, priority: 'High' },
  { name: 'Services', ratio: 12, priority: 'Medium' },
  { name: 'UI Components', ratio: 8, priority: 'Low' },
  { name: 'Utils', ratio: 5, priority: 'Low' },
];

// Sample data: Issue type breakdown
const issueTypeData = [
  { name: 'Code Smells', count: 450, percentage: 40 },
  { name: 'Complexity', count: 280, percentage: 25 },
  { name: 'Documentation', count: 170, percentage: 15 },
  { name: 'Testing', count: 113, percentage: 10 },
  { name: 'Security', count: 56, percentage: 5 },
  { name: 'Performance', count: 56, percentage: 5 },
];

export function TechnicalDebtTrend() {
  return (
    <div className="my-8">
      <h3 className="text-xl font-semibold text-accent mb-4">📉 Technical Debt Reduction</h3>
      <MetricLineChart
        data={debtTrendData}
        lines={[
          { dataKey: 'ratio', name: 'Debt Ratio (%)', color: '#E89841' },
        ]}
        xAxisLabel="Quarter"
        yAxisLabel="Debt Ratio (%)"
        targetLine={8}
        height={350}
      />
      <p className="text-sm text-foreground-secondary mt-2">
        Sample data showing improvement from 12.4% to 7.2% debt ratio over 6 quarters.
        The green line represents the Good threshold (8%). Slight uptick in Q6 warrants
        monitoring—ensure new code isn&apos;t introducing debt faster than it&apos;s being paid down.
      </p>
    </div>
  );
}

export function TechnicalDebtGauge() {
  return (
    <div className="my-8">
      <h3 className="text-xl font-semibold text-accent mb-4">🎯 Current Debt Ratio</h3>
      <MetricGauge
        value={7.2}
        max={20}
        label="Technical Debt Ratio"
        unit="%"
        ranges={[
          { min: 0, max: 25, color: '#6FCF97', label: 'Excellent' },
          { min: 25, max: 50, color: '#4A90E2', label: 'Good' },
          { min: 50, max: 75, color: '#F2C94C', label: 'Concerning' },
          { min: 75, max: 100, color: '#EB5757', label: 'Critical' },
        ]}
      />
      <p className="text-sm text-foreground-secondary mt-2">
        Current debt ratio of 7.2% is in the Excellent range. Continue with 20% sprint
        allocation to debt reduction and enforce quality gates on new code to maintain
        this healthy level.
      </p>
    </div>
  );
}

export function TechnicalDebtByModule() {
  return (
    <div className="my-8">
      <h3 className="text-xl font-semibold text-accent mb-4">🔥 Debt by Module (Priority for Refactoring)</h3>
      <MetricBarChart
        data={moduleDebtData}
        bars={[
          { dataKey: 'ratio', name: 'Debt Ratio (%)', color: '#E89841' },
        ]}
        xAxisLabel="Module"
        yAxisLabel="Debt Ratio (%)"
        height={350}
      />
      <p className="text-sm text-foreground-secondary mt-2">
        Auth (45%) and Payments (38%) modules are critical priorities for refactoring.
        These high-risk areas should be addressed first given their importance to core
        functionality. Plan dedicated refactoring sprints to reduce these to &lt; 15%.
      </p>
    </div>
  );
}

export function TechnicalDebtIssueTypes() {
  return (
    <div className="my-8">
      <h3 className="text-xl font-semibold text-accent mb-4">🔍 Issue Type Breakdown</h3>
      <MetricBarChart
        data={issueTypeData}
        bars={[
          { dataKey: 'count', name: 'Issue Count', color: '#E89841' },
        ]}
        xAxisLabel="Issue Type"
        yAxisLabel="Count"
        height={300}
      />
      <p className="text-sm text-foreground-secondary mt-2">
        Code Smells (40%) and Complexity issues (25%) make up 65% of technical debt.
        Focus on refactoring duplicated code, breaking down large functions, and reducing
        cyclomatic complexity to make the biggest impact.
      </p>
    </div>
  );
}
